import { api, type Category, type HistoryItem, type LibraryItem } from '../api';
import { getCurrentRole, requireAuthOrRedirect } from '../auth';
import { canAccess } from '../roles';
import { renderNav } from '../nav';
import { confirmDialog, coverEl, qs, toast, workHref, pluginContentTypes, clampTwoLines, refreshPrettyHrefs } from '../utils';
import { warmSlugCache } from '../pretty-cache';

const SORT_KEY = 'library-sort';
const TAB_KEY = 'library-tab';
const TAB_BY_TYPE_KEY = 'library-tab-by-type';
const TYPE_KEY = 'library-type';
type SortMode = 'name' | 'added' | 'updated' | 'unread';
type ContentType = 'manga' | 'novel' | 'anime';

const UNCAT_TAB = '__uncat__';

const TYPE_LABELS: Record<ContentType, string> = {
  manga: 'Манґи/Манхви',
  novel: 'Новели/Романи',
  anime: 'Аніме',
};
/** Порядок пунктів у випадаючому меню кнопки-пігулки — саме такий, як просили. */
const TYPE_ORDER: ContentType[] = ['manga', 'novel', 'anime'];

export async function initLibrary() {
  if (!(await requireAuthOrRedirect())) return;
  renderNav('library');
  // Гостьовий акаунт (tier_1_subscriber) має лише бібліотеку "Новели": перемикача типів нема, інші категорії
  // приховані. Сервер так само віддає лише новели й відхиляє решту (routes/library.ts, routes/categories.ts).
  const novelOnly = !canAccess(await getCurrentRole(), 'library.non-novel');

  // Для anime-творів картки й кнопка "Продовжити" ведуть на окрему anime.html,
  // а не novel.html — цю мапу заповнює один спільний запит /api/plugins.
  const contentTypes = await pluginContentTypes();

  // --- DOM ---
  const grid = document.getElementById('grid') as HTMLElement;
  const empty = document.getElementById('empty') as HTMLElement;
  const tabsEl = document.getElementById('category-tabs') as HTMLElement;
  const tabsEditBtn = document.getElementById('category-tabs-edit') as HTMLButtonElement;

  const titleRow = document.getElementById('lib-title-row') as HTMLElement;
  const searchBar = document.getElementById('lib-searchbar') as HTMLElement;
  const searchToggle = document.getElementById('lib-search-toggle') as HTMLButtonElement;
  const searchClose = document.getElementById('lib-search-close') as HTMLButtonElement;
  const searchInput = document.getElementById('search') as HTMLInputElement;
  const sortBtn = document.getElementById('lib-sort-btn') as HTMLButtonElement;
  const menuBtn = document.getElementById('lib-menu-btn') as HTMLButtonElement;

  const sortOverlay = document.getElementById('lib-sort-overlay') as HTMLElement;
  const sortClose = document.getElementById('lib-sort-close') as HTMLButtonElement;

  const menuDropdown = document.getElementById('lib-menu-dropdown') as HTMLElement;
  const menuRefreshAll = document.getElementById('lib-menu-refresh-all') as HTMLButtonElement;
  const menuRefreshCat = document.getElementById('lib-menu-refresh-cat') as HTMLButtonElement;
  const menuRandom = document.getElementById('lib-menu-random') as HTMLButtonElement;

  const selectRow = document.getElementById('lib-select-row') as HTMLElement;
  const selectClose = document.getElementById('lib-select-close') as HTMLButtonElement;
  const selectLabel = document.getElementById('lib-select-label') as HTMLElement;
  const selectActions = document.getElementById('lib-select-actions') as HTMLElement;
  const selectAllBtn = document.getElementById('lib-select-all') as HTMLButtonElement;
  const selectInvertBtn = document.getElementById('lib-select-invert') as HTMLButtonElement;
  const selectBar = document.getElementById('lib-select-bar') as HTMLElement;
  const lsbAssign = document.getElementById('lsb-assign') as HTMLButtonElement;
  const lsbRead = document.getElementById('lsb-read') as HTMLButtonElement;
  const lsbUnread = document.getElementById('lsb-unread') as HTMLButtonElement;
  const lsbMore = document.getElementById('lsb-more') as HTMLButtonElement;
  const lsbMoreDropdown = document.getElementById('lsb-more-dropdown') as HTMLElement;
  const lsbMoreRemove = document.getElementById('lsb-more-remove') as HTMLButtonElement;

  const manageOverlay = document.getElementById('category-manage-overlay') as HTMLElement;
  const manageClose = document.getElementById('category-manage-close') as HTMLButtonElement;
  const manageList = document.getElementById('category-manage-list') as HTMLElement;
  const addOpenBtn = document.getElementById('category-add-open') as HTMLButtonElement;

  const editOverlay = document.getElementById('category-edit-overlay') as HTMLElement;
  const editTitle = document.getElementById('category-edit-title') as HTMLElement;
  const nameInput = document.getElementById('category-name-input') as HTMLInputElement;
  const checkUpdatesInput = document.getElementById('category-check-updates') as HTMLInputElement;
  const editCancel = document.getElementById('category-edit-cancel') as HTMLButtonElement;
  const editSave = document.getElementById('category-edit-save') as HTMLButtonElement;

  const assignOverlay = document.getElementById('assign-categories-overlay') as HTMLElement;
  const assignClose = document.getElementById('assign-categories-close') as HTMLButtonElement;
  const assignList = document.getElementById('assign-categories-list') as HTMLElement;
  const assignApply = document.getElementById('assign-categories-apply') as HTMLButtonElement;

  // напівпрозора кнопка-пігулка внизу по центру: перемикач "власної" бібліотеки
  // (Манґи/Манхви / Новели/Романи / Аніме) з випадаючим контекстним меню
  const typeBtn = document.getElementById('lib-type-btn') as HTMLButtonElement;
  const typeLabel = document.getElementById('lib-type-label') as HTMLElement;
  const typeDropdown = document.getElementById('lib-type-dropdown') as HTMLElement;
  const typeFabWrap = document.querySelector('.lib-type-fab-wrap') as HTMLElement;

  // --- стан ---
  let items: LibraryItem[] = [];
  let categories: Category[] = [];
  let historyByNovel = new Map<string, HistoryItem>();
  // Якщо повернулись сюди з картки твору (яка веде за принципом
  // Бібліотека > Сторінка твору > Бібліотека) — відновлюємо саме ту
  // категорію/сортування/пошук, з яких вийшли, а не типові значення. Це
  // працює навіть якщо браузер не зміг відновити сторінку з bfcache і
  // довантажив її заново.
  const tabParam = qs('tab');
  const sortParam = qs('sort') as SortMode | null;
  const qParam = qs('q');
  const typeParam = qs('type') as ContentType | null;

  // За замовчуванням — Манґи/Манхви (як просили), доки користувач сам не обере інше.
  let activeType: ContentType = novelOnly
    ? 'novel'
    : typeParam && TYPE_ORDER.includes(typeParam)
      ? typeParam
      : ((localStorage.getItem(TYPE_KEY) as ContentType) || 'manga');
  if (typeParam && !novelOnly) localStorage.setItem(TYPE_KEY, typeParam);
  // Перемикач типів прихований у HTML (клас gated); показуємо лише тим, кому доступні не лише новели.
  if (!novelOnly) typeFabWrap.classList.remove('gated');

  // Кожен тип контенту (Манґи/Манхви, Новели/Романи, Аніме) має свій набір
  // категорій — тож і "остання активна категорія" запам'ятовується ОКРЕМО
  // для кожного типу (а не в одному спільному ключі, як раніше), інакше
  // перемикання типу шукало категорію з попереднього типу серед категорій
  // нового й, не знайшовши такої, завжди скидало на "Типово".
  function loadTabByType(): Partial<Record<ContentType, string>> {
    try {
      const raw = localStorage.getItem(TAB_BY_TYPE_KEY);
      return raw ? (JSON.parse(raw) as Partial<Record<ContentType, string>>) : {};
    } catch {
      return {};
    }
  }
  const tabByType: Partial<Record<ContentType, string>> = loadTabByType();
  // Міграція зі старого спільного ключа (лишався від версії до цього фікса) —
  // застосовуємо його лише до типу, що й так активний зараз, один раз.
  if (!tabByType[activeType]) {
    const legacyTab = localStorage.getItem(TAB_KEY);
    if (legacyTab) tabByType[activeType] = legacyTab;
  }

  let activeTab: string = tabParam || tabByType[activeType] || UNCAT_TAB;
  if (tabParam) tabByType[activeType] = tabParam;
  localStorage.setItem(TAB_BY_TYPE_KEY, JSON.stringify(tabByType));
  let sortMode: SortMode = sortParam || (localStorage.getItem(SORT_KEY) as SortMode) || 'added';
  if (sortParam) localStorage.setItem(SORT_KEY, sortParam);
  let selectionMode = false;
  const selected = new Set<string>();
  const cardEls = new Map<string, HTMLElement>();
  let editingCategoryId: string | null = null;

  /** Твори ВЛАСНОЇ бібліотеки поточного типу контенту (Манґи/Манхви, Новели/Романи чи Аніме). */
  function itemsOfType(): LibraryItem[] {
    return items.filter((i) => i.contentType === activeType);
  }

  /** Категорії ВЛАСНОЇ бібліотеки поточного типу контенту. */
  function catsOfType(): Category[] {
    return categories.filter((c) => c.contentType === activeType);
  }

  /** Малює кнопку-пігулку у стані поточного обраного типу (текст + колір). */
  function paintTypeButton() {
    typeLabel.textContent = TYPE_LABELS[activeType];
    typeBtn.classList.remove('type-manga', 'type-novel', 'type-anime');
    typeBtn.classList.add(`type-${activeType}`);
    typeDropdown.querySelectorAll<HTMLElement>('.lib-type-option').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.type === activeType);
    });
  }

  function closeTypeDropdown() {
    typeDropdown.style.display = 'none';
  }

  typeBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    typeDropdown.style.display = typeDropdown.style.display === 'none' ? 'flex' : 'none';
  });
  document.addEventListener('click', (e) => {
    if (typeDropdown.style.display !== 'none' && !typeDropdown.contains(e.target as Node) && e.target !== typeBtn) {
      closeTypeDropdown();
    }
  });
  typeDropdown.querySelectorAll<HTMLButtonElement>('.lib-type-option').forEach((btn) => {
    btn.addEventListener('click', () => {
      const next = btn.dataset.type as ContentType;
      closeTypeDropdown();
      if (next === activeType) return;
      activeType = next;
      localStorage.setItem(TYPE_KEY, activeType);
      // Категорії/таби іншого типу — інший набір, тож відновлюємо ОСТАННЮ
      // активну категорію САМЕ для цього типу (tabByType), а не завжди
      // "Типово" — якщо для цього типу ще ніколи не обирали категорію,
      // tabByType[next] буде undefined і UNCAT_TAB лишається розумним
      // фолбеком. Якщо ж збережена категорія цього типу вже не існує
      // (видалена) — renderTabs() нижче сам виправить на перший наявний таб.
      setActiveTab(tabByType[next] || UNCAT_TAB);
      paintTypeButton();
      renderTabs();
      renderGrid();
    });
  });

  async function load() {
    try {
      const [libRes, catRes, historyRes] = await Promise.all([
        api.library(),
        api.categories(),
        api.history().catch(() => []),
      ]);
      items = libRes;
      categories = catRes;
      historyByNovel = new Map(historyRes.map((h) => [h.novelId, h]));
    } catch (err) {
      toast((err as Error).message);
    }
    renderTabs();
    renderGrid();
  }

  // ---------------- таби категорій ----------------

  function renderTabs() {
    tabsEl.innerHTML = '';
    const cats = catsOfType();
    if (!cats.length) {
      tabsEl.style.display = 'none';
      return;
    }

    // Порожні категорії ховаємо з табів (нема сенсу перемикатись на
    // категорію без жодного твору) — окрім тієї, на якій користувач зараз
    // стоїть: вона не має зникати "з-під ніг", наприклад одразу після того,
    // як з неї прибрали останній твір.
    const typeItems = itemsOfType();
    const uncatCount = typeItems.filter((i) => !i.categoryIds.length).length;
    const tabs: { id: string; name: string }[] = [];
    if (uncatCount > 0 || activeTab === UNCAT_TAB) tabs.push({ id: UNCAT_TAB, name: 'Типово' });
    for (const c of cats) {
      const count = typeItems.filter((i) => i.categoryIds.includes(c.id)).length;
      if (count > 0 || activeTab === c.id) tabs.push({ id: c.id, name: c.name });
    }

    if (!tabs.length) {
      tabsEl.style.display = 'none';
      return;
    }
    tabsEl.style.display = 'flex';

    if (!tabs.some((t) => t.id === activeTab)) setActiveTab(tabs[0].id);

    for (const t of tabs) {
      const btn = document.createElement('button');
      btn.className = `category-tab${t.id === activeTab ? ' active' : ''}`;
      btn.textContent = t.name;
      btn.addEventListener('click', () => {
        setActiveTab(t.id);
        renderTabs();
        renderGrid();
      });
      tabsEl.appendChild(btn);
    }
  }

  /** Змінює активну категорію й запам'ятовує її ОКРЕМО для поточного типу контенту (tabByType) — щоб при наступному відкритті бібліотеки чи перемиканні типу відновлювалась саме вона, а не завжди "Типово". */
  function setActiveTab(id: string) {
    activeTab = id;
    tabByType[activeType] = id;
    localStorage.setItem(TAB_BY_TYPE_KEY, JSON.stringify(tabByType));
  }

  // ---------------- сітка ----------------

  function visibleItems(): LibraryItem[] {
    let list = itemsOfType();
    if (activeTab === UNCAT_TAB) list = list.filter((i) => !i.categoryIds.length);
    else list = list.filter((i) => i.categoryIds.includes(activeTab));

    const q = searchInput.value.trim().toLowerCase();
    if (q) list = list.filter((i) => i.name.toLowerCase().includes(q));

    const sorted = [...list];
    if (sortMode === 'name') {
      sorted.sort((a, b) => a.name.localeCompare(b.name, 'uk'));
    } else if (sortMode === 'added') {
      sorted.sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime());
    } else if (sortMode === 'updated') {
      sorted.sort((a, b) => {
        const at = historyByNovel.get(a.id)?.updatedAt;
        const bt = historyByNovel.get(b.id)?.updatedAt;
        if (!at && !bt) return 0;
        if (!at) return 1;
        if (!bt) return -1;
        return new Date(bt).getTime() - new Date(at).getTime();
      });
    } else if (sortMode === 'unread') {
      sorted.sort((a, b) => b.unreadCount - a.unreadCount);
    }
    return sorted;
  }

  /** Параметри повернення в поточний стан бібліотеки (тип/категорія/сортування/пошук). */
  function returnContextSuffix(): string {
    const params = new URLSearchParams({ type: activeType, tab: activeTab, sort: sortMode });
    const q = searchInput.value.trim();
    if (q) params.set('q', q);
    return `&${params.toString()}`;
  }

  function novelUrl(item: LibraryItem): string {
    return workHref(item.pluginId, item.path, contentTypes[item.pluginId], `&from=library${returnContextSuffix()}`);
  }

  function renderGrid() {
    const list = visibleItems();
    grid.innerHTML = '';
    cardEls.clear();
    if (!list.length) {
      empty.style.display = 'flex';
      grid.style.display = 'none';
      return;
    }
    empty.style.display = 'none';
    grid.style.display = 'grid';

    for (const item of list) {
      const a = document.createElement('a');
      a.className = `cardlink lib-card${selected.has(item.id) ? ' selected' : ''}`;
      a.href = novelUrl(item);
      a.dataset.prettyPlugin = item.pluginId;
      a.dataset.prettyPath = item.path;
      if (contentTypes[item.pluginId]) a.dataset.prettyType = contentTypes[item.pluginId];
      a.dataset.prettyExtra = `&from=library${returnContextSuffix()}`;

      const cover = coverEl(item.name, item.cover, item.id);
      if (item.unreadCount > 0) {
        const badge = document.createElement('span');
        badge.className = 'badge';
        badge.textContent = String(item.unreadCount);
        cover.appendChild(badge);
      }
      const scrim = document.createElement('div');
      scrim.className = 'lib-card-scrim';
      const title = document.createElement('div');
      title.className = 'lib-card-title';
      title.textContent = item.name;
      cover.appendChild(scrim);
      cover.appendChild(title);
      const selRing = document.createElement('div');
      selRing.className = 'lib-card-selring';
      cover.appendChild(selRing);

      a.appendChild(cover);
      attachCardInteractions(a, item.id);
      cardEls.set(item.id, a);
      grid.appendChild(a);
      clampTwoLines(title);
    }
    warmSlugCache(
      list.map((item) => ({ pluginId: item.pluginId, path: item.path, title: item.name })),
      () => refreshPrettyHrefs(grid),
    );
  }

  /**
   * Один обробник на картку: звичайний тап перемикає вибір (лише в режимі
   * вибору), довге утримання вмикає режим вибору. КРИТИЧНО: після
   * спрацювання довгого натискання браузер все одно шле звичайний "click" по
   * відпусканню пальця — якщо не погасити саме цей клік, він одразу ж знову
   * знімає щойно поставлений вибір (звідси й "обведення миттю зникає").
   */
  function attachCardInteractions(el: HTMLElement, id: string) {
    let timer: number | undefined;
    let moved = false;
    let longPressFired = false;

    el.addEventListener('pointerdown', (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      moved = false;
      timer = window.setTimeout(() => {
        if (!moved && !selectionMode) {
          longPressFired = true;
          enterSelectionMode(id);
        }
      }, 450);
    });
    el.addEventListener('pointermove', (e: PointerEvent) => {
      if (Math.abs(e.movementX) + Math.abs(e.movementY) > 6) moved = true;
    });
    const clearTimer = () => {
      if (timer) window.clearTimeout(timer);
    };
    el.addEventListener('pointerup', clearTimer);
    el.addEventListener('pointerleave', clearTimer);
    el.addEventListener('contextmenu', (e) => e.preventDefault());

    el.addEventListener('click', (e) => {
      if (longPressFired) {
        longPressFired = false;
        e.preventDefault();
        return;
      }
      if (!selectionMode) return;
      e.preventDefault();
      toggleSelect(id);
    });
  }

  // ---------------- пошук ----------------

  function openSearch() {
    titleRow.style.display = 'none';
    searchBar.style.display = 'flex';
    searchInput.focus();
  }
  function closeSearch() {
    searchInput.value = '';
    searchBar.style.display = 'none';
    titleRow.style.display = 'flex';
    renderGrid();
  }
  searchToggle.addEventListener('click', openSearch);
  searchClose.addEventListener('click', closeSearch);
  searchInput.addEventListener('input', renderGrid);

  // ---------------- сортування ----------------

  function paintSortRows() {
    sortOverlay.querySelectorAll<HTMLElement>('.reading-mode-row').forEach((row) => {
      row.classList.toggle('active', row.dataset.sort === sortMode);
    });
  }
  sortBtn.addEventListener('click', () => {
    paintSortRows();
    sortOverlay.style.display = 'flex';
  });
  sortClose.addEventListener('click', () => (sortOverlay.style.display = 'none'));
  sortOverlay.addEventListener('click', (e) => {
    if (e.target === sortOverlay) sortOverlay.style.display = 'none';
  });
  sortOverlay.querySelectorAll<HTMLElement>('.reading-mode-row').forEach((row) => {
    row.addEventListener('click', () => {
      sortMode = row.dataset.sort as SortMode;
      localStorage.setItem(SORT_KEY, sortMode);
      sortOverlay.style.display = 'none';
      renderGrid();
    });
  });

  // ---------------- випадаюче меню (три крапки) ----------------

  function closeMenuDropdown() {
    menuDropdown.style.display = 'none';
  }
  menuBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    menuDropdown.style.display = menuDropdown.style.display === 'none' ? 'flex' : 'none';
  });
  document.addEventListener('click', (e) => {
    if (menuDropdown.style.display !== 'none' && !menuDropdown.contains(e.target as Node) && e.target !== menuBtn) {
      closeMenuDropdown();
    }
  });

  async function runCheck(categoryId: string | undefined) {
    toast('Перевіряю оновлення…');
    try {
      const res = await api.checkUpdates(categoryId);
      toast(res.newUpdates ? `Знайдено нових розділів: ${res.newUpdates}` : 'Нових розділів не знайдено');
    } catch (err) {
      toast((err as Error).message);
    }
  }

  menuRefreshAll.addEventListener('click', () => {
    closeMenuDropdown();
    runCheck(undefined);
  });

  menuRefreshCat.addEventListener('click', () => {
    closeMenuDropdown();
    const categoryId = activeTab !== UNCAT_TAB ? activeTab : undefined;
    runCheck(categoryId);
  });

  menuRandom.addEventListener('click', () => {
    closeMenuDropdown();
    const list = visibleItems();
    if (!list.length) {
      toast('У бібліотеці порожньо');
      return;
    }
    const item = list[Math.floor(Math.random() * list.length)];
    location.href = novelUrl(item);
  });

  tabsEditBtn.addEventListener('click', () => {
    renderManageList();
    manageOverlay.style.display = 'flex';
  });

  // ---------------- режим вибору творів ----------------

  function enterSelectionMode(initialId?: string) {
    selectionMode = true;
    selected.clear();
    titleRow.style.display = 'none';
    searchBar.style.display = 'none';
    selectRow.style.display = 'flex';
    selectBar.style.display = 'flex';
    typeFabWrap.style.display = 'none';
    if (initialId) {
      selected.add(initialId);
      paintCardSelected(initialId);
    }
    paintSelectionHeader();
  }

  function exitSelectionMode() {
    selectionMode = false;
    closeLsbMoreDropdown();
    const wasSelected = [...selected];
    selected.clear();
    wasSelected.forEach((id) => paintCardSelected(id));
    selectRow.style.display = 'none';
    selectBar.style.display = 'none';
    typeFabWrap.style.display = '';
    titleRow.style.display = 'flex';
  }

  function paintSelectionHeader() {
    const n = selected.size;
    selectLabel.textContent = n > 0 ? String(n) : 'Оберіть твори';
    selectActions.style.display = n > 0 ? 'flex' : 'none';
  }

  function paintCardSelected(id: string) {
    const el = cardEls.get(id);
    if (el) el.classList.toggle('selected', selected.has(id));
  }

  function toggleSelect(id: string) {
    if (selected.has(id)) selected.delete(id);
    else selected.add(id);
    paintCardSelected(id);
    // Зняли останній обраний твір — виходимо з режиму вибору.
    if (selected.size === 0) {
      exitSelectionMode();
      return;
    }
    paintSelectionHeader();
  }

  selectClose.addEventListener('click', exitSelectionMode);

  selectAllBtn.addEventListener('click', () => {
    visibleItems().forEach((i) => {
      selected.add(i.id);
      paintCardSelected(i.id);
    });
    paintSelectionHeader();
  });

  selectInvertBtn.addEventListener('click', () => {
    visibleItems().forEach((i) => {
      if (selected.has(i.id)) selected.delete(i.id);
      else selected.add(i.id);
      paintCardSelected(i.id);
    });
    // Інверсія залишила 0 обраних — виходимо з режиму вибору.
    if (selected.size === 0) {
      exitSelectionMode();
      return;
    }
    paintSelectionHeader();
  });

  lsbRead.addEventListener('click', async () => {
    if (!selected.size) return;
    const ids = [...selected];
    toast('Позначаю прочитаними…');
    try {
      await api.markLibraryRead(ids);
      toast('Позначено прочитаними');
    } catch (err) {
      toast((err as Error).message);
    }
    exitSelectionMode();
    await load();
  });

  lsbUnread.addEventListener('click', async () => {
    if (!selected.size) return;
    const ids = [...selected];
    try {
      await api.markLibraryUnread(ids);
      toast('Позначено непрочитаними');
    } catch (err) {
      toast((err as Error).message);
    }
    exitSelectionMode();
    await load();
  });

  // "Ще" (три крапки) в панелі дій вибору — контекстне меню з вилученням
  // обраних творів з бібліотеки.
  function closeLsbMoreDropdown() {
    lsbMoreDropdown.style.display = 'none';
  }
  lsbMore.addEventListener('click', (e) => {
    e.stopPropagation();
    lsbMoreDropdown.style.display = lsbMoreDropdown.style.display === 'none' ? 'flex' : 'none';
  });
  document.addEventListener('click', (e) => {
    if (lsbMoreDropdown.style.display !== 'none' && !lsbMoreDropdown.contains(e.target as Node) && e.target !== lsbMore) {
      closeLsbMoreDropdown();
    }
  });

  lsbMoreRemove.addEventListener('click', async () => {
    closeLsbMoreDropdown();
    if (!selected.size) return;
    const ids = [...selected];
    const ok = await confirmDialog({
      title: ids.length > 1 ? `Вилучити ${ids.length} творів з бібліотеки?` : 'Вилучити твір з бібліотеки?',
      message: 'Історію читання це не зачепить.',
      confirmText: 'Вилучити',
      danger: true,
    });
    if (!ok) return;
    try {
      await Promise.all(ids.map((id) => api.removeFromLibrary(id)));
      toast(ids.length > 1 ? 'Твори вилучено з бібліотеки' : 'Твір вилучено з бібліотеки');
    } catch (err) {
      toast((err as Error).message);
    }
    exitSelectionMode();
    await load();
  });

  // ---------------- призначення категорій обраним творам ----------------

  lsbAssign.addEventListener('click', () => {
    if (!selected.size) return;
    if (!catsOfType().length) {
      toast('Спершу створіть категорію в «Керувати категоріями»');
      return;
    }
    renderAssignList();
    assignOverlay.style.display = 'flex';
  });

  function renderAssignList() {
    assignList.innerHTML = '';
    const selectedItems = items.filter((i) => selected.has(i.id));
    for (const c of catsOfType()) {
      const row = document.createElement('label');
      row.className = 'assign-cat-row';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.dataset.id = c.id;
      cb.checked = selectedItems.length > 0 && selectedItems.every((i) => i.categoryIds.includes(c.id));
      const span = document.createElement('span');
      span.textContent = c.name;
      row.appendChild(cb);
      row.appendChild(span);
      assignList.appendChild(row);
    }
  }

  assignClose.addEventListener('click', () => (assignOverlay.style.display = 'none'));
  assignOverlay.addEventListener('click', (e) => {
    if (e.target === assignOverlay) assignOverlay.style.display = 'none';
  });

  assignApply.addEventListener('click', async () => {
    const add: string[] = [];
    const remove: string[] = [];
    assignList.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => {
      if (cb.checked) add.push(cb.dataset.id!);
      else remove.push(cb.dataset.id!);
    });
    try {
      await api.assignLibraryCategories([...selected], add, remove);
      toast('Категорію призначено');
    } catch (err) {
      toast((err as Error).message);
    }
    assignOverlay.style.display = 'none';
    exitSelectionMode();
    await load();
  });

  // ---------------- керування категоріями ----------------

  manageClose.addEventListener('click', () => (manageOverlay.style.display = 'none'));
  manageOverlay.addEventListener('click', (e) => {
    if (e.target === manageOverlay) manageOverlay.style.display = 'none';
  });

  function renderManageList() {
    manageList.innerHTML = '';
    const cats = catsOfType();
    if (!cats.length) {
      const p = document.createElement('div');
      p.className = 'category-manage-empty';
      p.textContent = 'Категорій ще немає.';
      manageList.appendChild(p);
      return;
    }
    for (const c of cats) {
      const row = document.createElement('div');
      row.className = 'category-manage-row';
      row.dataset.id = c.id;

      const handle = document.createElement('div');
      handle.className = 'category-drag-handle';
      handle.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 8h16M4 16h16"/></svg>';

      const name = document.createElement('div');
      name.className = 'category-manage-name';
      name.textContent = c.name;

      const editBtn = document.createElement('button');
      editBtn.className = 'iconbtn';
      editBtn.setAttribute('aria-label', 'Редагувати категорію');
      editBtn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>';
      editBtn.addEventListener('click', () => openEditCategory(c));

      const delBtn = document.createElement('button');
      delBtn.className = 'iconbtn icon-danger';
      delBtn.setAttribute('aria-label', 'Видалити категорію');
      delBtn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7V4h6v3m-8 0 1 13a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2l1-13"/></svg>';
      delBtn.addEventListener('click', () => deleteCategory(c));

      row.appendChild(handle);
      row.appendChild(name);
      row.appendChild(editBtn);
      row.appendChild(delBtn);
      manageList.appendChild(row);
    }
    makeSortable();
  }

  async function deleteCategory(c: Category) {
    const ok = await confirmDialog({
      title: `Видалити категорію «${c.name}»?`,
      message: 'Твори з неї нікуди не зникнуть.',
      confirmText: 'Видалити',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteCategory(c.id);
      categories = categories.filter((x) => x.id !== c.id);
      items.forEach((i) => (i.categoryIds = i.categoryIds.filter((id) => id !== c.id)));
      renderManageList();
      renderTabs();
      renderGrid();
      toast('Категорію видалено');
    } catch (err) {
      toast((err as Error).message);
    }
  }

  // просте перетягування рядків за ручку (свайп по вертикалі, з живим обміном місцями)
  function makeSortable() {
    let dragEl: HTMLElement | null = null;
    let startY = 0;

    manageList.querySelectorAll<HTMLElement>('.category-drag-handle').forEach((handle) => {
      handle.onpointerdown = (e: PointerEvent) => {
        const row = handle.closest('.category-manage-row') as HTMLElement | null;
        if (!row) return;
        e.preventDefault();
        dragEl = row;
        startY = e.clientY;
        row.classList.add('dragging');
        row.style.position = 'relative';
        row.style.zIndex = '5';
        try {
          handle.setPointerCapture(e.pointerId);
        } catch {
          /* ігноруємо — не критично */
        }

        const onMove = (ev: PointerEvent) => {
          if (!dragEl) return;
          const dy = ev.clientY - startY;
          dragEl.style.transform = `translateY(${dy}px)`;
          const dragRect = dragEl.getBoundingClientRect();
          const dragMid = dragRect.top + dragRect.height / 2;

          const prev = dragEl.previousElementSibling as HTMLElement | null;
          const next = dragEl.nextElementSibling as HTMLElement | null;
          if (prev) {
            const r = prev.getBoundingClientRect();
            if (dragMid < r.top + r.height / 2) {
              manageList.insertBefore(dragEl, prev);
              startY = ev.clientY;
              dragEl.style.transform = 'translateY(0px)';
              return;
            }
          }
          if (next) {
            const r = next.getBoundingClientRect();
            if (dragMid > r.top + r.height / 2) {
              manageList.insertBefore(dragEl, next.nextSibling);
              startY = ev.clientY;
              dragEl.style.transform = 'translateY(0px)';
            }
          }
        };

        const onUp = async () => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          if (!dragEl) return;
          dragEl.style.transform = '';
          dragEl.style.position = '';
          dragEl.style.zIndex = '';
          dragEl.classList.remove('dragging');
          dragEl = null;
          const orderedIds = Array.from(manageList.querySelectorAll<HTMLElement>('.category-manage-row')).map(
            (el) => el.dataset.id!,
          );
          orderedIds.forEach((id, idx) => {
            const cat = categories.find((c) => c.id === id);
            if (cat) cat.order = idx;
          });
          categories.sort((a, b) => a.order - b.order);
          renderTabs();
          try {
            await api.reorderCategories(orderedIds);
          } catch (err) {
            toast((err as Error).message);
          }
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      };
    });
  }

  // ---------------- додати / редагувати категорію ----------------

  function openAddCategory() {
    editingCategoryId = null;
    editTitle.textContent = 'Додати категорію';
    editSave.textContent = 'Додати';
    nameInput.value = '';
    checkUpdatesInput.checked = true;
    editOverlay.style.display = 'flex';
    setTimeout(() => nameInput.focus(), 50);
  }

  function openEditCategory(c: Category) {
    editingCategoryId = c.id;
    editTitle.textContent = 'Редагувати категорію';
    editSave.textContent = 'Зберегти';
    nameInput.value = c.name;
    checkUpdatesInput.checked = c.checkUpdates;
    editOverlay.style.display = 'flex';
    setTimeout(() => nameInput.focus(), 50);
  }

  addOpenBtn.addEventListener('click', openAddCategory);
  editCancel.addEventListener('click', () => (editOverlay.style.display = 'none'));
  editOverlay.addEventListener('click', (e) => {
    if (e.target === editOverlay) editOverlay.style.display = 'none';
  });

  editSave.addEventListener('click', async () => {
    const name = nameInput.value.trim();
    if (!name) {
      nameInput.focus();
      toast('Назва категорії обов’язкова');
      return;
    }
    const checkUpdates = checkUpdatesInput.checked;
    try {
      if (editingCategoryId) {
        const updated = await api.updateCategory(editingCategoryId, { name, checkUpdates });
        const idx = categories.findIndex((c) => c.id === editingCategoryId);
        if (idx >= 0) categories[idx] = updated;
      } else {
        const created = await api.createCategory(name, checkUpdates, activeType);
        categories.push(created);
      }
      editOverlay.style.display = 'none';
      renderManageList();
      renderTabs();
      renderGrid();
    } catch (err) {
      toast((err as Error).message);
    }
  });

  if (qParam) {
    searchInput.value = qParam;
    openSearch();
  }

  paintTypeButton();
  await load();

  // Якщо браузер відновив цю сторінку з bfcache (реальна кнопка/жест
  // "Назад", а не клік по посиланню) — виконання JS не перезапускається,
  // тож `items` лишається тим, яким був ДО того, як користувач пішов з
  // бібліотеки. Якщо за цей час на сервері щось змінилось (відкрили твір і
  // прочитали розділи, спрацювала перевірка оновлень, завершився імпорт
  // тощо) — лічильники непрочитаного в кутку плиток лишаються застарілими,
  // поки не зробити повне перезавантаження сторінки вручну. Перезапитуємо
  // дані щоразу, коли сторінку саме ВІДНОВЛЕНО з bfcache.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) void load();
  });
}
